<template>
  <v-sheet class="fs-scheduler">
    <v-toolbar flat class="scheduler-toolbar">
      <v-toolbar-title class="mr-4 font-weight-bold">
        Truck Schedule
      </v-toolbar-title>

      <v-btn outlined class="mr-2" @click="setToday">Today</v-btn>
      <v-btn icon @click="prev"><v-icon>mdi-chevron-left</v-icon></v-btn>
      <v-btn icon @click="next"><v-icon>mdi-chevron-right</v-icon></v-btn>

      <v-toolbar-title class="ml-4">{{ title }}</v-toolbar-title>
      <v-spacer />

      <v-select v-model="viewType" :items="viewOptions" dense outlined hide-details style="max-width: 160px"
        @change="onViewChange" />
    </v-toolbar>

    <v-calendar v-if="viewType === 'month'" ref="cal" v-model="value" :type="viewType" :events="calendarEvents"
      :event-color="(e) => e.color" @change="updateTitle" />

    <div v-else-if="viewType === 'day'" class="lanes-wrapper">
      <div ref="timeHeader" class="time-header" @scroll.passive="onHeaderScroll">
        <div class="time-header-inner" :style="{ width: timelineTotalWidthPx }">
          <div v-for="h in hours" :key="`tick-${h}`" class="time-tick" :style="{ left: hourToPx(h) }">
            {{ pad2(h) }}:00
          </div>

          <div v-for="m in gridMinutes" :key="`grid-${m}`" class="grid-line" :style="{ left: minutesToPx(m) }" />
        </div>
      </div>

      <div ref="rows" class="rows" @scroll.passive="onRowsScroll">
        <div v-for="lane in laneItems" :key="lane.id" class="lane">
          <div class="lane-title">
            <v-chip small class="lane-chip" :style="laneChipStyle(laneSummary(lane.id))">
              <v-avatar left size="18" :color="lane.color" class="white--text">
                {{ lane.avatarText }}
              </v-avatar>

              <span class="lane-chip__name">{{ lane.name }}</span>
              <span class="lane-chip__count">{{ laneSummary(lane.id).count }}</span>
              <span class="status-dot" :style="{ background: statusColor(laneSummary(lane.id).status) }" />
            </v-chip>
          </div>

          <div class="row-inner" :class="{ 'is-drop-target': dropTargetKey === lane.id }"
            :style="{ width: timelineTotalWidthPx }" @dragenter.prevent="onDragEnterDropZone(lane.id, $event)"
            @dragover.prevent="onDragOverDropZone"
            @drop.stop.prevent="handleDropEquip(dropLaneEquipId(lane.id), $event)">
            <div v-if="dropTargetKey === lane.id" class="drop-label"
              :style="{ background: hexToRgba(lane.color, 0.9) }">
              <span class="drop-label__avatar" :style="{ background: lane.color }">
                {{ lane.avatarText }}
              </span>
              {{ lane.name }}
            </div>

            <div v-for="ev in dayEventsByLane(lane.id)" :key="ev.id" class="event-block" :class="eventClasses(ev)"
              :style="eventStyleDay(ev)" :draggable="!ev.isMaintenance"
              @dragstart="!ev.isMaintenance && onDragStart(ev, $event)" @dragend="clearDrag"
              @click.stop="openEventDialog(ev)">
              <div class="event-title">
                <span v-if="isTinyEvent(ev)" class="event-num">#{{ ev.bookingID }}</span>
                <span v-else>{{ ev.title }}</span>
              </div>

              <div v-if="!isTinyEvent(ev)" class="event-sub">
                {{ formatHM(ev.startMs) }} - {{ formatHM(ev.endMs) }}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div v-else class="week-wrapper">
      <div class="equip-panel">
        <div class="equip-panel__title">Trucks</div>

        <div v-for="lane in laneItems" :key="lane.id" class="equip-item"
          :class="{ 'is-drop-target': dropTargetKey === lane.id }"
          @dragenter.prevent="onDragEnterDropZone(lane.id, $event)" @dragover.prevent="onDragOverDropZone"
          @drop.stop.prevent="handleDropEquip(dropLaneEquipId(lane.id), $event)">
          <v-chip small class="lane-chip" :style="laneChipStyle(laneSummary(lane.id))"
            :class="{ 'is-selected': !lane.isUnassigned && selectedEquipIdSet.has(lane.id) }"
            @click="!lane.isUnassigned && toggleEquipFilter(lane.id)">
            <v-avatar left size="18" :color="lane.color" class="white--text">
              {{ lane.avatarText }}
            </v-avatar>

            <span class="lane-chip__name">{{ lane.name }}</span>
            <span class="lane-chip__count">{{ laneSummary(lane.id).count }}</span>
            <span class="status-dot" :style="{ background: statusColor(laneSummary(lane.id).status) }" />
          </v-chip>

          <div v-if="lane.isUnassigned && weekEventsByLane(lane.id).length" class="unassigned-list">
            <div v-for="ev in weekEventsByLane(lane.id)" :key="ev.id" class="unassigned-item"
              :draggable="!ev.isMaintenance" @dragstart="!ev.isMaintenance && onDragStart(ev, $event)"
              @dragend="clearDrag" @click.stop="openEventDialog(ev)">
              <div class="unassigned-item__title">{{ ev.title }}</div>
              <div class="unassigned-item__sub">
                {{ formatHM(ev.startMs) }} - {{ formatHM(ev.endMs) }}
              </div>
            </div>
          </div>
        </div>

        <div class="equip-panel__hint">
          Drag event → drop onto equipment to assign / drop Unassigned to unassign.
        </div>
      </div>

      <div class="week-grid">
        <div class="week-grid__header">
          <div class="week-grid__corner"></div>

          <div class="week-grid__days">
            <div v-for="d in weekDays" :key="d.key" class="day-col-head">
              <div class="dow">{{ d.dow }}</div>
              <div class="dom">{{ d.dom }}</div>
            </div>
          </div>
        </div>

        <div ref="weekScroll" class="week-grid__body" @scroll.passive="onWeekScroll">
          <div class="time-col" :style="{ height: weekBodyHeightPx }">
            <div v-for="h in hours" :key="`wh-${h}`" class="time-label" :style="{ top: weekMinToTop(h * 60) }">
              {{ pad2(h) }}:00
            </div>
          </div>

          <div class="days-area" :style="{ height: weekBodyHeightPx }">
            <div v-for="(d, dayIdx) in weekDays" :key="`col-${d.key}`" class="day-col"
              :class="{ 'is-drop-target': dropTargetKey === `week-day-${dayIdx}` }" :data-day-index="dayIdx"
              @dragenter.prevent="onDragEnterWeekDay(dayIdx, $event)"
              @dragover.prevent="onDragOverWeekDay(dayIdx, $event)"
              @drop.stop.prevent="handleDropWeekDay(d, dayIdx, $event)">
              <div v-for="h in hours" :key="`line-${d.key}-${h}`" class="hour-line"
                :style="{ top: weekMinToTop(h * 60) }" />

              <div v-for="ev in eventsForWeekDayWithLayout(d.startMs, d.endMs)" :key="ev.id" class="week-event"
                :style="eventStyleWeek(ev)" :draggable="!ev.isMaintenance"
                @dragstart="!ev.isMaintenance && onDragStart(ev, $event)" @click.stop="openEventDialog(ev)"
                @dragend="clearDrag">
                <div class="week-event__title">
                  {{ ev.equipID || "Unassigned" }} - {{ ev.bookingID }}
                </div>

                <div class="week-event__sub">
                  {{ formatHM(ev.startMs) }} - {{ formatHM(ev.endMs) }}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div class="toast-stack">
      <transition-group name="toast">
        <div v-for="t in toasts" :key="t.id" class="toast-item" :class="`toast--${t.color}`">
          <span class="toast-item__text">{{ t.text }}</span>
          <button class="toast-item__close" @click="removeToast(t.id)">✕</button>
        </div>
      </transition-group>
    </div>

    <v-dialog v-model="dialog" max-width="500">
      <v-card v-if="selectedEvent">
        <v-card-title>
          {{ equipNameById(selectedEvent.equipID) }}
        </v-card-title>

        <v-card-text>
          <div>
            <b>Truck:</b>
            {{ equipNameById(selectedEvent.equipID) }}
          </div>

          <div>
            <b>Start:</b>
            {{ formatDateTime(selectedEvent.startMs) }}
          </div>

          <div>
            <b>End:</b>
            {{ formatDateTime(selectedEvent.endMs) }}
          </div>

          <div>
            <b>{{ selectedEvent.isMaintenance ? "Maintenance Job" : "Booking" }}:</b>
            {{ selectedEvent.isMaintenance ? selectedEvent.jobNum : selectedEvent.bookingID }}
          </div>
        </v-card-text>

        <v-card-actions>
          <v-spacer />
          <v-btn text @click="dialog = false">Close</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-sheet>
</template>

<script>
import { mapGetters } from "vuex";

const EQUIP_PALETTE = [
  "#031716",
  "#032F30",
  "#0A7075",
  "#0C969C",
  "#6BA3BE",
  "#274D60",
  "#0B525B",
  "#144552",
  "#1B6B6F",
  "#2CA6A4",
  "#3D8B8A",
  "#5CB8B2",
];

const DND_MIME = "text/plain";
const DEFAULT_UNASSIGNED_COLOR = "#757575";
const EQUIP_UNASSIGNED_KEY = "unassigned";

function formatDateLocal(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

function normalizeAssignee(v) {
  const s = v == null ? "" : String(v).trim();
  if (!s || s === "0") return null;
  return s;
}

function toSecondsFromMidnight(date) {
  if (!(date instanceof Date) || isNaN(date.getTime())) {
    return 0;
  }

  return (
    date.getHours() * 3600 +
    date.getMinutes() * 60 +
    date.getSeconds()
  );
}

function buildEventKey({ bookingID, equipID, jobNum }) {
  return [

    String(equipID ?? EQUIP_UNASSIGNED_KEY),
    String(bookingID ?? ""),
    String(jobNum ?? ""),
  ].join("|");
}
function getDurationHours(dateStart, dateEnd) {
  if (!dateStart || !dateEnd) return 0;

  const start = new Date(dateStart);
  const end = new Date(dateEnd);

  if (isNaN(start) || isNaN(end)) return 0;

  const diffMs = end - start;

  return diffMs / (1000 * 60 * 60); // ms → hours
}
export default {
  name: "EquipScheduling",

  data() {
    return {
      EQUIP_UNASSIGNED_KEY,
      value: formatDateLocal(new Date()),
      viewType: "week",
      viewOptions: [
        { text: "Day", value: "day" },
        { text: "Week", value: "week" },
        { text: "Month", value: "month" },
      ],
      title: "",

      dayStartHour: 0,
      dayEndHour: 24,
      pxPerMin: 2,
      laneGutterPx: 240,
      tinyEventWidthPx: 90,

      weekPxPerMin: 1,

      dragEventId: null,
      dropTargetKey: null,

      syncFromHeader: false,
      syncFromRows: false,

      selectedEquipIds: [],
      toasts: [],
      pendingEventOverrides: {},

      dialog: false,
      selectedEvent: null,
    };
  },

  computed: {
    ...mapGetters("Data", ["getTransportPlans", "getEquipList"]),

    equipList() {
      return (this.getEquipList || []).map((item) => {
        const id = String(item.Equip_EquipID);
        const description = String(item.Equip_Description || "").trim();
        return {
          id,
          name: description && description !== id ? `${id} - ${description}` : id,
          avatarText: this.initials(item.Equip_EquipID),
          color: this.equipColor(id),
        };
      });
    },

    equipIdSet() {
      return new Set(this.equipList.map((x) => x.id));
    },

    laneItems() {
      return [
        {
          id: EQUIP_UNASSIGNED_KEY,
          name: "Unassigned",
          color: "#6B7280",
          avatarText: this.initials("Unassigned"),
          isUnassigned: true,
        },
        ...this.equipList.map((x) => ({
          ...x,
          isUnassigned: false,
        })),
      ];
    },

    calls() {
      return (this.getTransportPlans || []).map((row, idx) => this.buildEvent(row, idx));
    },

    callsByLaneMap() {
      const map = new Map();

      for (const call of this.calls) {
        const key = this.laneKey(call.equipID);
        if (!map.has(key)) map.set(key, []);
        map.get(key).push(call);
      }

      for (const arr of map.values()) {
        arr.sort((a, b) => a.startMs - b.startMs);
      }

      return map;
    },

    totalMinutes() {
      return (this.dayEndHour - this.dayStartHour) * 60;
    },

    timelineWidthPx() {
      return this.totalMinutes * this.pxPerMin;
    },

    timelineTotalWidthPx() {
      return `${this.laneGutterPx + this.timelineWidthPx}px`;
    },

    hours() {
      return Array.from(
        { length: this.dayEndHour - this.dayStartHour },
        (_, i) => this.dayStartHour + i
      );
    },

    gridMinutes() {
      const arr = [];
      for (let m = 0; m <= this.totalMinutes; m += 30) arr.push(m);
      return arr;
    },

    calendarEvents() {
      return this.calls.map((c) => ({
        name: c.title,
        start: new Date(c.startMs),
        end: new Date(c.endMs),
        color: c.color || "#0C969C",
      }));
    },

    weekRangeMs() {
      return this.getRangeMs(this.value, "week");
    },

    dayRangeMs() {
      return this.getRangeMs(this.value, "day");
    },

    weekDays() {
      const { startMs } = this.weekRangeMs;

      return Array.from({ length: 7 }, (_, i) => {
        const d = new Date(startMs + i * 86400000);
        const dayStart = new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();

        return {
          key: `${d.getFullYear()}-${d.getMonth() + 1}-${d.getDate()}`,
          startMs: dayStart,
          endMs: dayStart + 86400000,
          dow: ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"][d.getDay()],
          dom: d.getDate(),
        };
      });
    },

    weekBodyHeightPx() {
      return `${this.totalMinutes * this.weekPxPerMin}px`;
    },

    selectedEquipIdSet() {
      return new Set(this.selectedEquipIds);
    },
  },

  mounted() {
    this.initialize();
  },

  methods: {
    async initialize() {
      await this.loadData();
      this.$nextTick(() => this.updateTitle());
    },

    async loadData() {
      await Promise.all([
        this.$store.dispatch("Data/getTransportPlans"),
        this.$store.dispatch("Data/getEquipList"),
      ]);
      this.updateTitle();
    },

    async reloadCallsFromServer() {
      await this.$store.dispatch("Data/getTransportPlans");
    },

    buildEvent(row, idx) {
      const bookingID = String(row.UD08_Key1 || "").trim();
      const schedDate = row.UD08_Date02;
      const endDate = row.UD08_Date03;
      const schedTime = Number(
        row.Calculated_StartTime ?? row.StartTime ?? row.UD08_Number09 ?? 0
      );
      const safeStartTime = schedTime > 0 ? schedTime : 8 * 3600;
      const endTime = Number(
        row.Calculated_EndTime ?? row.EndTime ?? row.UD08_Number10 ?? 0
      );
      const safeEndTime =
        endTime > 0 ? endTime : safeStartTime + 3600;
      const startMs = this.combineDateAndSecondsToMs(schedDate, safeStartTime);
      const endMs = this.combineDateAndSecondsToMs(schedDate, safeEndTime);

      const baseEquipID = normalizeAssignee(row.Equip_EquipID);
      const equipDescription = String(row.Equip_Equip_Description || row.Equip_Description || "").trim();
      const equipLabel =
        baseEquipID && equipDescription && equipDescription !== baseEquipID
          ? `${baseEquipID} - ${equipDescription}`
          : (baseEquipID || "Unassigned");
      const jobNum = row.UD08_Key1;
      const isMaintenance =
        String(row.Calculated_Type || "").trim().toUpperCase() === "MNT";

      const baseKey = buildEventKey({
        equipID: baseEquipID,
        bookingID,
        jobNum,
      });

      const override = this.pendingEventOverrides[baseKey] || {};

      const equipID = Object.prototype.hasOwnProperty.call(override, "equipID")
        ? override.equipID
        : baseEquipID;
      const durationHours = getDurationHours(schedDate, endDate);
      const finalStartMs = override.startMs ?? startMs;
      const finalEndMs = override.endMs ?? endMs;

      const finalKey = buildEventKey({
        equipID,
        bookingID,
        jobNum,
      });

      return {
        rowIndex: idx,
        id: finalKey,
        originalId: baseKey,
        bookingID,
        title: `${bookingID || "No Booking"} - ${equipLabel}`,
        startMs: finalStartMs,
        endMs: finalEndMs,
        equipID,
        equipName: equipLabel,
        equipDescription,
        color: isMaintenance
          ? "#EAB308"
          : equipID
            ? this.equipColor(equipID)
            : DEFAULT_UNASSIGNED_COLOR,
        jobNum,
        durationHours,
        isMaintenance,
        calculatedType: row.Calculated_Type || "",
      };
    },

    laneKey(equipID) {
      return equipID ? String(equipID) : EQUIP_UNASSIGNED_KEY;
    },

    dropLaneEquipId(laneId) {
      return laneId === EQUIP_UNASSIGNED_KEY ? null : laneId;
    },

    callsByLane(laneId) {
      return this.callsByLaneMap.get(this.laneKey(laneId)) || [];
    },

    dayEventsByLane(laneId) {
      const { startMs, endMs } = this.dayRangeMs;
      return this.callsByLane(laneId).filter((x) => x.startMs >= startMs && x.startMs < endMs);
    },

    weekEventsByLane(laneId) {
      const { startMs, endMs } = this.weekRangeMs;
      return this.callsByLane(laneId).filter((x) => x.startMs >= startMs && x.startMs < endMs);
    },

    laneSummary(laneId) {
      const list =
        this.viewType === "week"
          ? this.weekEventsByLane(laneId)
          : this.dayEventsByLane(laneId);

      const count = list.length;

      return {
        count,
        status: count > 0 ? "busy" : "available",
      };
    },

    eventsForWeekDay(dayStartMs, dayEndMs) {
      const { startMs, endMs } = this.weekRangeMs;

      return this.calls.filter((x) => {
        const inWeek = x.startMs >= startMs && x.startMs < endMs;
        const inDay = x.startMs >= dayStartMs && x.startMs < dayEndMs;
        const allowEquip =
          !x.equipID ||
          this.selectedEquipIdSet.size === 0 ||
          this.selectedEquipIdSet.has(String(x.equipID));

        return inWeek && inDay && allowEquip;
      });
    },

    equipNameById(equipID) {
      if (!equipID) return "Unassigned";
      return this.equipList.find((x) => x.id === String(equipID))?.name || "Unassigned";
    },

    toggleEquipFilter(equipID) {
      const id = String(equipID);

      if (this.selectedEquipIdSet.has(id)) {
        this.selectedEquipIds = this.selectedEquipIds.filter((x) => x !== id);
      } else {
        this.selectedEquipIds = [...this.selectedEquipIds, id];
      }
    },

    showToast(text, color = "success") {
      const id = Date.now() + Math.random();
      this.toasts.push({ id, text, color });
      setTimeout(() => this.removeToast(id), 6000);
    },

    removeToast(id) {
      const idx = this.toasts.findIndex((t) => t.id === id);
      if (idx !== -1) this.toasts.splice(idx, 1);
    },

    extractImporterError(parsed) {
      try {
        const rows = parsed?.Data?.Tables?.[0]?.Rows ?? [];
        const failed = rows.find((r) => !r.IsSuccess && r.ImportError?.Message);
        if (!failed) return null;
        const inner = JSON.parse(failed.ImportError.Message);
        return inner?.ErrorMessage ?? failed.ImportError.Message;
      } catch {
        return null;
      }
    },

    makeImporterCallbacks(successMsg, failurePrefix) {
      return {
        onSuccess: () => this.showToast(successMsg, "success"),
        onFailure: (parsed) => {
          const detail = this.extractImporterError(parsed) ?? "Unknown error";
          this.showToast(`${failurePrefix}: ${detail}`, "error");
        },
      };
    },

    baseDayStartMs(dateLike) {
      if (!dateLike) return NaN;
      const d0 = new Date(dateLike);
      if (Number.isNaN(d0.getTime())) return NaN;
      return new Date(d0.getFullYear(), d0.getMonth(), d0.getDate()).getTime();
    },

    combineDateAndSecondsToMs(dateLike, secondsFromMidnight) {
      const base = this.baseDayStartMs(dateLike);
      if (Number.isNaN(base)) return NaN;
      return base + Number(secondsFromMidnight ?? 0) * 1000;
    },

    getRangeMs(valueDateStr, type) {
      const [y, m, d] = valueDateStr.split("-").map(Number);

      // 👇 FIX: tạo local date, không dùng string ISO
      const base = new Date(y, m - 1, d);

      const start = new Date(base);
      const end = new Date(base);

      if (type === "week") {
        const day = (start.getDay() + 6) % 7;
        start.setDate(start.getDate() - day);
        end.setDate(start.getDate() + 7);
      } else {
        end.setDate(start.getDate() + 1);
      }

      return { startMs: start.getTime(), endMs: end.getTime() };
    },

    updateTitle() {
      if (this.viewType === "month" && this.$refs.cal?.title) {
        this.title = this.$refs.cal.title;
        return;
      }

      if (this.viewType === "week") {
        const start = new Date(this.weekRangeMs.startMs);
        const end = new Date(this.weekRangeMs.endMs - 1);
        this.title = `${formatDateLocal(start)} ~ ${formatDateLocal(end)}`;
        return;
      }

      this.title = this.value;
    },

    onViewChange() {
      this.updateTitle();
    },

    prev() {
      if (this.viewType === "month" && this.$refs.cal) {
        this.$refs.cal.prev();
      } else {
        this.shiftRange(-1);
      }
      this.updateTitle();
    },

    next() {
      if (this.viewType === "month" && this.$refs.cal) {
        this.$refs.cal.next();
      } else {
        this.shiftRange(1);
      }
      this.updateTitle();
    },

    setToday() {
      this.value = formatDateLocal(new Date());
      this.updateTitle();
    },

    shiftRange(step) {
      const d = new Date(`${this.value}T00:00:00`);
      d.setDate(d.getDate() + step * (this.viewType === "week" ? 7 : 1));
      this.value = formatDateLocal(d);
    },

    onHeaderScroll() {
      if (this.syncFromRows) return;
      const h = this.$refs.timeHeader;
      const r = this.$refs.rows;
      if (!h || !r) return;
      this.syncFromHeader = true;
      r.scrollLeft = h.scrollLeft;
      this.syncFromHeader = false;
    },

    onRowsScroll() {
      if (this.syncFromHeader) return;
      const h = this.$refs.timeHeader;
      const r = this.$refs.rows;
      if (!h || !r) return;
      this.syncFromRows = true;
      h.scrollLeft = r.scrollLeft;
      this.syncFromRows = false;
    },

    onWeekScroll() { },

    setDragPayload(e, payloadObj) {
      const dt = e?.dataTransfer;
      if (!dt) return;
      const payload = JSON.stringify(payloadObj);
      dt.setData(DND_MIME, payload);
      dt.setData("text", payload);
      dt.effectAllowed = "move";
      dt.dropEffect = "move";
    },

    getDragPayload(e) {
      const dt = e?.dataTransfer;
      if (!dt) return null;

      const raw =
        dt.getData(DND_MIME) ||
        dt.getData("text") ||
        dt.getData("application/json");

      if (!raw) return null;

      try {
        return JSON.parse(raw);
      } catch {
        return null;
      }
    },

    onDragEnterDropZone(key, e) {
      this.dropTargetKey = key;
      if (e?.dataTransfer) e.dataTransfer.dropEffect = "move";
    },

    onDragOverDropZone(e) {
      if (e?.dataTransfer) e.dataTransfer.dropEffect = "move";
    },

    onDragStart(ev, e) {
      this.dragEventId = ev.id;
      this.setDragPayload(e, {
        id: ev.id,
        originalId: ev.originalId,
        bookingID: ev.bookingID,
        oldEquipId: ev.equipID ?? null,
        jobNum: ev.jobNum,
      });
    },

    clearDrag() {
      this.dragEventId = null;
      this.dropTargetKey = null;
    },

    setPendingEventOverride(eventKey, patch) {
      this.$set(this.pendingEventOverrides, eventKey, {
        ...(this.pendingEventOverrides[eventKey] || {}),
        ...patch,
      });
    },

    clearPendingEventOverride(eventKey) {
      this.$delete(this.pendingEventOverrides, eventKey);
    },

    async persistReassign(bookingID, oldEquipId, newEquipId) {
      try {
        // 1. Get UD08 record
        const getResp = await this.$store.dispatch("Data/callBO", {
          bo: "Ice.BO.UD08",
          method: "GetByID",
          body: {
            key1: String(bookingID),
            key2: "BookingList",
            key3: "",
            key4: "",
            key5: "",
          },
        });

        const ds = JSON.parse(JSON.stringify(getResp?.returnObj || {}));

        if (!ds?.UD08?.length) {
          throw new Error(`UD08 not found: ${bookingID}`);
        }

        const row = ds.UD08[0];

        // 2. Update Equipment (Character01)
        row.Character01 = newEquipId || ""; // null -> unassigned
        row.RowMod = "U";

        // 3. Save
        const updateResp = await this.$store.dispatch("Data/callBO", {
          bo: "Ice.BO.UD08",
          method: "Update",
          body: { ds },
        });

        if (updateResp?.error) {
          throw new Error(updateResp.error);
        }

        this.showToast(
          `Update equipment successfully #${bookingID}`,
          "success"
        );

        return updateResp;
      } catch (err) {
        console.error("persistReassign UD08 error", err);

        this.showToast(
          `Update equipment failed #${bookingID}`,
          "error"
        );

        throw err;
      }
    },

    async handleDropEquip(targetEquipId, e) {
      const drag = this.getDragPayload(e);
      if (!drag) return;

      const oldEquipId = drag.oldEquipId != null ? String(drag.oldEquipId) : null;
      const newEquipId = targetEquipId != null ? String(targetEquipId) : null;

      if (oldEquipId === newEquipId) {
        this.clearDrag();
        return;
      }

      const oldEvent = this.calls.find(
        (x) => x.id === drag.id || x.originalId === drag.originalId
      );

      if (!oldEvent) {
        this.clearDrag();
        return;
      }

      const eventKey = oldEvent.originalId || oldEvent.id;

      try {
        this.setPendingEventOverride(eventKey, { equipID: newEquipId });
        await this.persistReassign(oldEvent.bookingID, oldEquipId, newEquipId);

        this.reloadCallsFromServer();
        this.clearPendingEventOverride(eventKey);
        this.showToast(`Reassigned successfully #${oldEvent.bookingID}`, "success");
      } catch (err) {
        this.clearPendingEventOverride(eventKey);
        console.error("handleDropEquip error", err);
        this.showToast(`Reassign failed #${oldEvent.bookingID}`, "error");
      } finally {
        this.clearDrag();
      }
    },

    onDragEnterWeekDay(dayIdx, e) {
      this.dropTargetKey = `week-day-${dayIdx}`;
      if (e?.dataTransfer) e.dataTransfer.dropEffect = "move";
    },

    onDragOverWeekDay(dayIdx, e) {
      this.dropTargetKey = `week-day-${dayIdx}`;
      if (e?.dataTransfer) e.dataTransfer.dropEffect = "move";
    },

    roundToStep(min, step = 15) {
      return Math.round(min / step) * step;
    },

    getMinutesFromWeekDropEvent(dayEl, e) {
      const rect = dayEl.getBoundingClientRect();
      const y = e.clientY - rect.top;
      const rawMin = y / this.weekPxPerMin;
      return this.clamp(this.roundToStep(rawMin, 15), 0, 24 * 60);
    },

    async handleDropWeekDay(day, dayIdx, e) {
      const drag = this.getDragPayload(e);
      if (!drag) return;

      const oldEvent = this.calls.find(
        (x) => x.id === drag.id || x.originalId === drag.originalId
      );

      if (!oldEvent) {
        this.clearDrag();
        return;
      }

      if (oldEvent.isMaintenance) {
        this.showToast("Maintenance job cannot be moved", "warning");
        this.clearDrag();
        return;
      }

      const durationMs = oldEvent.endMs - oldEvent.startMs;
      const newStartMs =
        day.startMs + this.getMinutesFromWeekDropEvent(e.currentTarget, e) * 60 * 1000;
      const newEndMs = newStartMs + durationMs;
      const eventKey = oldEvent.originalId || oldEvent.id;

      try {
        this.setPendingEventOverride(eventKey, {
          startMs: newStartMs,
          endMs: newEndMs,
        });

        await this.persistMoveScheduleUd08({
          call: oldEvent,
          newStartMs,
          newEndMs,
        });

        await new Promise((r) => setTimeout(r, 300));

        await this.reloadCallsFromServer();

        this.clearPendingEventOverride(eventKey);
      } catch (err) {
        this.clearPendingEventOverride(eventKey);
        console.error("handleDropWeekDay error", err);
        this.showToast(`Schedule move failed #${oldEvent.bookingID}`, "error");
      } finally {
        this.clearDrag();
      }
    },
    async persistMoveScheduleUd08({ call, newStartMs, newEndMs }) {
      const bookingID = String(call.bookingID || "").trim();
      if (!bookingID) throw new Error("Invalid bookingID");

      const start = new Date(newStartMs);
      const end = new Date(newEndMs);

      const schedDate = `${start.getFullYear()}-${String(start.getMonth() + 1).padStart(2, "0")}-${String(start.getDate()).padStart(2, "0")}T00:00:00`;
      const schedTime = toSecondsFromMidnight(start);
      const endTime = toSecondsFromMidnight(end);

      const getResp = await this.$store.dispatch("Data/callBO", {
        bo: "Ice.BO.UD08",
        method: "GetByID",
        body: {
          key1: bookingID,
          key2: "BookingList",
          key3: "",
          key4: "",
          key5: "",
        },
      });

      const ds = JSON.parse(JSON.stringify(getResp?.returnObj || {}));

      if (!ds?.UD08?.length) {
        throw new Error(`UD08 not found: ${bookingID}`);
      }

      const row = ds.UD08[0];
      row.Date02 = schedDate;
      row.Date03 = schedDate;
      row.Number09 = schedTime;        // start seconds
      row.Number10 = endTime;          // end seconds
      row.RowMod = "U";

      const updateResp = await this.$store.dispatch("Data/callBO", {
        bo: "Ice.BO.UD08",
        method: "Update",
        body: { ds },
      });

      if (updateResp?.error) {
        throw new Error(updateResp.error);
      }

      this.showToast(`Move successfully #${bookingID}`, "success");

      return updateResp;
    },
    async UD08({ call, newStartMs }) {
      const bookingID = Number(call.bookingID || call.CallNum);
      if (!bookingID) throw new Error("Invalid Booking");

      const start = new Date(newStartMs);
      const schedDate = formatDateLocal(start);
      const schedTime = toSecondsFromMidnight(start);

      const getResp = await this.$store.dispatch("Data/callBO", {
        bo: "Ice.BO.UD08",
        method: "GetByID",
        body: { bookingID },
      });

      const ds = JSON.parse(JSON.stringify(getResp?.returnObj || {}));

      if (!ds?.FSCallhd?.length) {
        throw new Error(`Booking not found: ${bookingID}`);
      }

      const head = ds.FSCallhd[0];
      head.SchedDate = `${schedDate}T00:00:00`;
      head.SchedTime = schedTime;
      head.RowMod = "U";

      const updateResp = await this.$store.dispatch("Data/callBO", {
        bo: "Ice.BO.UD08",
        method: "Update",
        body: { ds },
      });

      if (updateResp?.error) {
        throw new Error(updateResp.error);
      }

      this.showToast(`Schedule updated successfully #${bookingID}`, "success");


      return updateResp;
    },

    eventWidthPx(ev) {
      const leftMin = this.toTimelineMin(ev.startMs);
      const rightMin = this.toTimelineMin(ev.endMs);
      const widthMin = Math.max(15, rightMin - leftMin);
      return widthMin * this.pxPerMin;
    },

    isTinyEvent(ev) {
      return this.eventWidthPx(ev) < this.tinyEventWidthPx;
    },

    eventClasses(ev) {
      return {
        "is-tiny": this.isTinyEvent(ev),
        "is-maintenance": ev.isMaintenance,
      };
    },

    pad2(n) {
      return String(n).padStart(2, "0");
    },

    clamp(v, min, max) {
      return Math.max(min, Math.min(max, v));
    },

    msToOffsetMin(ms) {
      const d = new Date(ms);
      return d.getHours() * 60 + d.getMinutes() - this.dayStartHour * 60;
    },

    toTimelineMin(ms) {
      return this.clamp(this.msToOffsetMin(ms), 0, this.totalMinutes);
    },

    minutesToPx(min) {
      return `${this.laneGutterPx + min * this.pxPerMin}px`;
    },

    hourToPx(hour) {
      return this.minutesToPx((hour - this.dayStartHour) * 60);
    },

    formatHM(ms) {
      const d = new Date(ms);
      return `${this.pad2(d.getHours())}:${this.pad2(d.getMinutes())}`;
    },

    formatDateTime(ms) {
      const d = new Date(ms);
      return `${formatDateLocal(d)} ${this.pad2(d.getHours())}:${this.pad2(
        d.getMinutes()
      )}`;
    },

    weekMinToTop(min) {
      return `${min * this.weekPxPerMin}px`;
    },

    toDayMinute(ms) {
      const d = new Date(ms);
      return d.getHours() * 60 + d.getMinutes();
    },

    eventStyleWeek(ev) {
      const main = ev.isMaintenance ? "#D4A106" : (ev.color || "#0C969C");
      const bg = ev.isMaintenance
        ? "rgba(250, 204, 21, 0.28)"
        : this.hexToRgba(main, 0.18);

      const startMin = this.toDayMinute(ev.startMs);
      const endMin = this.toDayMinute(ev.endMs);
      const height = Math.max(18, (endMin - startMin) * this.weekPxPerMin);

      const totalCols = Math.max(1, ev._overlapTotalCols || 1);
      const col = ev._overlapCol || 0;
      const gap = 6;
      const usableWidth = `calc((100% - ${gap * (totalCols + 1)}px) / ${totalCols})`;
      const left = `calc(${gap}px + (${col} * (${usableWidth} + ${gap}px)))`;

      return {
        top: `${startMin * this.weekPxPerMin}px`,
        height: `${height}px`,
        width: usableWidth,
        left,
        background: bg,
        borderLeft: `4px solid ${main}`,
        color: ev.isMaintenance ? "#5B4500" : "#053238",
      };
    },
    eventStyleDay(ev) {
      const main = ev.isMaintenance ? "#D4A106" : (ev.color || "#0C969C");
      const bg = ev.isMaintenance
        ? "rgba(250, 204, 21, 0.28)"
        : this.hexToRgba(main, 0.18);

      const leftMin = this.toTimelineMin(ev.startMs);
      const rightMin = this.toTimelineMin(ev.endMs);
      const widthMin = Math.max(15, rightMin - leftMin);

      return {
        left: `${this.laneGutterPx + leftMin * this.pxPerMin}px`,
        width: `${widthMin * this.pxPerMin}px`,
        top: "50%",
        transform: "translateY(-50%)",
        height: "40px",
        background: bg,
        color: ev.isMaintenance ? "#5B4500" : "#053238",
        borderLeft: `4px solid ${main}`,
        borderRadius: "10px",
        boxSizing: "border-box",
      };
    },

    statusColor(status) {
      if (status === "busy") return "#F78C6A";
      if (status === "available") return "#06D7A0";
      return "#9E9E9E";
    },

    laneChipStyle(lane) {
      const main = lane.color || "#6B7280";
      return {
        background: this.hexToRgba(main, 0.14),
        color: "#0b1f23",
        border: "none",
      };
    },

    initials(name) {
      const parts = String(name || "").trim().split(/\s+/);
      if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
      return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase();
    },

    hexToRgba(hex, alpha = 0.15) {
      const h = String(hex || "").replace("#", "");
      if (h.length !== 6) return `rgba(0,0,0,${alpha})`;
      const bigint = parseInt(h, 16);
      const r = (bigint >> 16) & 255;
      const g = (bigint >> 8) & 255;
      const b = bigint & 255;
      return `rgba(${r}, ${g}, ${b}, ${alpha})`;
    },

    stringHash(str) {
      return [...String(str)].reduce((a, c) => a + c.charCodeAt(0), 0);
    },

    equipColor(key) {
      return EQUIP_PALETTE[this.stringHash(key) % EQUIP_PALETTE.length];
    },

    openEventDialog(ev) {
      this.selectedEvent = ev;
      this.dialog = true;
    },
    eventsForWeekDayWithLayout(dayStartMs, dayEndMs) {
      const events = this.eventsForWeekDay(dayStartMs, dayEndMs)
        .slice()
        .sort((a, b) => {
          if (a.startMs !== b.startMs) return a.startMs - b.startMs;
          return a.endMs - b.endMs;
        });

      const groups = [];
      let currentGroup = [];
      let currentGroupMaxEnd = -Infinity;

      for (const ev of events) {
        if (!currentGroup.length) {
          currentGroup = [ev];
          currentGroupMaxEnd = ev.endMs;
          continue;
        }

        if (ev.startMs < currentGroupMaxEnd) {
          currentGroup.push(ev);
          currentGroupMaxEnd = Math.max(currentGroupMaxEnd, ev.endMs);
        } else {
          groups.push(this.buildOverlapColumns(currentGroup));
          currentGroup = [ev];
          currentGroupMaxEnd = ev.endMs;
        }
      }

      if (currentGroup.length) {
        groups.push(this.buildOverlapColumns(currentGroup));
      }

      return groups.flat();
    },

    buildOverlapColumns(events) {
      const columns = [];
      const laidOut = [];

      for (const ev of events) {
        let placedCol = -1;

        for (let col = 0; col < columns.length; col++) {
          const lastInCol = columns[col][columns[col].length - 1];
          if (lastInCol.endMs <= ev.startMs) {
            columns[col].push(ev);
            placedCol = col;
            break;
          }
        }

        if (placedCol === -1) {
          columns.push([ev]);
          placedCol = columns.length - 1;
        }

        laidOut.push({
          ...ev,
          _overlapCol: placedCol,
        });
      }

      const totalCols = columns.length;

      return laidOut.map((ev) => ({
        ...ev,
        _overlapTotalCols: totalCols,
      }));
    },
  },
};
</script>

<style scoped>
.fs-scheduler {
  width: 100%;
  background: #fff;
}

.scheduler-toolbar {
  border-bottom: 1px solid #eceff1;
}

.lanes-wrapper {
  border-left: 1px solid #eee;
}

.time-header {
  height: 48px;
  overflow-x: auto;
  overflow-y: hidden;
  border-bottom: 1px solid #eee;
  background: #fff;
}

.time-header-inner {
  position: relative;
  height: 48px;
}

.time-tick {
  position: absolute;
  top: 14px;
  transform: translateX(-50%);
  font-size: 12px;
  color: #666;
}

.grid-line {
  position: absolute;
  top: 0;
  bottom: 0;
  width: 1px;
  background: #f0f0f0;
}

.rows {
  height: calc(100vh - 168px);
  overflow: auto;
  background: #fff;
}

.lane {
  position: relative;
  display: block;
}

.lane-title {
  position: absolute;
  left: 20px;
  top: 50%;
  transform: translateY(-50%);
  z-index: 4;
  pointer-events: none;
}

.lane-title .v-chip {
  pointer-events: auto;
}

.row-inner {
  position: relative;
  height: 64px;
  background: #fff;
  transition: box-shadow 0.15s ease, background 0.15s ease;
}

.row-inner::after {
  content: "";
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
  height: 1px;
  background: #f0f0f0;
  pointer-events: none;
  z-index: 1;
}

.row-inner.is-drop-target {
  box-shadow: inset 0 0 0 2px #0c969c;
  background: rgba(12, 150, 156, 0.05);
}

.drop-label {
  position: sticky;
  left: 248px;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  height: 28px;
  margin-top: 18px;
  padding: 0 10px 0 4px;
  border-radius: 14px;
  color: #fff;
  font-size: 12px;
  font-weight: 600;
  white-space: nowrap;
  pointer-events: none;
  z-index: 3;
}

.drop-label__avatar {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 22px;
  height: 22px;
  border-radius: 50%;
  border: 1.5px solid rgba(255, 255, 255, 0.7);
  font-size: 10px;
  font-weight: 700;
  color: #fff;
  flex-shrink: 0;
}

.lane-chip {
  border-radius: 999px;
  font-weight: 700;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 0 10px 0 6px;
  min-height: 36px;
}

.lane-chip__name {
  max-width: 240px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.lane-chip__count {
  min-width: 22px;
  height: 18px;
  padding: 0 6px;
  border-radius: 999px;
  font-size: 11px;
  font-weight: 800;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: rgba(0, 0, 0, 0.06);
}

.status-dot {
  width: 10px;
  height: 10px;
  border-radius: 999px;
  display: inline-block;
  flex-shrink: 0;
}

.event-block {
  position: absolute;
  z-index: 2;
  border-radius: 10px;
  padding: 6px 10px;
  cursor: grab;
  user-select: none;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 2px;
  overflow: hidden;
}

.event-title {
  font-size: 12px;
  font-weight: 800;
  line-height: 1.1;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.event-sub {
  font-size: 11px;
  opacity: 0.8;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.event-block.is-tiny {
  padding: 0 8px;
  align-items: center;
  text-align: center;
}

.event-num {
  font-weight: 900;
}

.week-wrapper {
  display: grid;
  grid-template-columns: 300px 1fr;
  height: calc(100vh - 120px);
  border-top: 1px solid #eee;
}

.equip-panel {
  border-right: 1px solid #eee;
  padding: 12px;
  overflow: auto;
  background: #fff;
}

.equip-panel__title {
  font-weight: 800;
  font-size: 16px;
  margin-bottom: 10px;
}

.equip-panel__hint {
  font-size: 12px;
  opacity: 0.7;
  margin-top: 12px;
}

.equip-item {
  margin-bottom: 16px;
  border-radius: 10px;
  padding: 4px;
  transition: box-shadow 0.15s ease, background 0.15s ease;
}

.equip-item.is-drop-target {
  box-shadow: 0 0 0 2px #0c969c;
  background: rgba(12, 150, 156, 0.05);
}

.lane-chip.is-selected {
  outline: 2px solid rgba(12, 150, 156, 0.35);
}

.unassigned-list {
  margin: 10px 0 8px;
  display: grid;
  gap: 8px;
}

.unassigned-item {
  border: 1px solid #eee;
  border-radius: 12px;
  padding: 8px 10px;
  cursor: grab;
  background: #fff;
}

.unassigned-item__title {
  font-size: 12px;
  font-weight: 900;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.unassigned-item__sub {
  font-size: 11px;
  opacity: 0.75;
  margin-top: 2px;
}

.week-grid {
  display: grid;
  grid-template-rows: 56px 1fr;
  min-width: 880px;
  background: #fff;
}

.week-grid__header {
  display: grid;
  grid-template-columns: 64px 1fr;
  border-bottom: 1px solid #eee;
  background: #fff;
  position: sticky;
  top: 0;
  z-index: 5;
}

.week-grid__corner {
  border-right: 1px solid #eee;
}

.week-grid__days {
  display: grid;
  grid-template-columns: repeat(7, 1fr);
}

.day-col-head {
  padding: 8px 10px;
  border-right: 1px solid #eee;
}

.day-col-head:last-child {
  border-right: none;
}

.dow {
  font-size: 12px;
  opacity: 0.75;
}

.dom {
  font-size: 18px;
  font-weight: 800;
  margin-top: 2px;
}

.week-grid__body {
  display: grid;
  grid-template-columns: 64px 1fr;
  overflow: auto;
  position: relative;
}

.time-col {
  position: relative;
  border-right: 1px solid #eee;
  background: #fff;
}

.time-label {
  position: absolute;
  left: 8px;
  transform: translateY(-50%);
  font-size: 11px;
  color: #777;
}

.days-area {
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  position: relative;
  background: #fff;
}

.day-col {
  position: relative;
  border-right: 1px solid #eee;
}

.day-col:last-child {
  border-right: none;
}

.day-col.is-drop-target {
  background: rgba(12, 150, 156, 0.06);
  box-shadow: inset 0 0 0 2px #0c969c;
}

.hour-line {
  position: absolute;
  left: 0;
  right: 0;
  height: 1px;
  background: #f0f0f0;
}

.week-event {
  position: absolute;
  left: 6px;
  border-radius: 10px;
  padding: 6px 8px;
  box-sizing: border-box;
  cursor: grab;
  overflow: hidden;
  min-height: 22px;
}

.week-event__title {
  font-size: 12px;
  font-weight: 900;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.week-event__sub {
  font-size: 11px;
  opacity: 0.8;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.toast-stack {
  position: fixed;
  right: 16px;
  bottom: 16px;
  z-index: 9999;
  display: flex;
  flex-direction: column-reverse;
  gap: 8px;
  pointer-events: none;
}

.toast-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  min-width: 280px;
  max-width: 420px;
  padding: 12px 16px;
  border-radius: 4px;
  color: #fff;
  font-size: 14px;
  box-shadow: 0 3px 10px rgba(0, 0, 0, 0.25);
  pointer-events: auto;
  margin: 5px;
}

.toast--success {
  background: #4caf50;
}

.toast--error {
  background: #f44336;
}

.toast--info {
  background: #2196f3;
}

.toast--warning {
  background: #ff9800;
}

.toast-item__text {
  flex: 1;
  line-height: 1.4;
}

.toast-item__close {
  background: none;
  border: none;
  color: rgba(255, 255, 255, 0.8);
  font-size: 14px;
  cursor: pointer;
  padding: 0 0 0 4px;
  flex-shrink: 0;
}

.toast-item__close:hover {
  color: #fff;
}

.toast-enter-active,
.toast-leave-active {
  transition: all 0.25s ease;
}

.toast-enter,
.toast-leave-to {
  opacity: 0;
  transform: translateX(40px);
}
</style>
